<template>
    <div class="map-wrapper" :style="cemeteriesMapContainerStyle">
        <div id="cemeteries-map" class="map">
            <MapLayerSwitcher
                :map="mapRef"
                :disable-change="menuRouteModalVisible"
                v-if="!!mapRef"
                :layer-switcher-data="layerSwitcherData"
                @update-layer-data="handleLayerSwitcherUpdate"
            />
        </div>

        <!-- Consulta no mapa (lado da camada sepultura, topo): busca por nome ou CPF em todos os cemitérios -->
        <div class="map-consulta-wrapper">
            <div class="map-consulta-search-wrapper">
                <a-button
                    class="map-icon-btn map-consulta-toggle-btn"
                    title="Pesquisar"
                    aria-label="Pesquisar"
                    @click="toggleConsultaSearch"
                >
                    <template #icon>
                        <SearchOutlined class="menu-icon" />
                    </template>
                </a-button>
                <a-input
                    v-if="showConsultaSearch"
                    v-model:value="consultaSearchTerm"
                    placeholder="Buscar por nome ou CPF..."
                    allow-clear
                    class="map-consulta-input"
                    @input="onConsultaSearchInput"
                />
            </div>
            <div v-if="consultaSearching" class="map-consulta-loading">
                <i class="fas fa-spinner fa-spin"></i> Buscando...
            </div>
            <div v-else-if="consultaSearchTerm && consultaResults.length > 0" class="map-consulta-results">
                <div
                    v-for="(item, idx) in consultaResults"
                    :key="idx"
                    class="map-consulta-result-item"
                    @click="selectConsultaResult(item)"
                >
                    <span class="map-consulta-label">Falecido:</span> {{ item.defunct_name }} —
                    <span class="map-consulta-label">Cemitério:</span> {{ item.cemetery_name }}
                </div>
            </div>
            <div v-else-if="consultaSearchTerm && consultaSearchTerm.length >= 2 && !consultaSearching && consultaResults.length === 0" class="map-consulta-empty">
                Nenhum resultado encontrado.
            </div>
        </div>

        <!-- Ícones dos submenus do menu Cemitérios (canto superior esquerdo, estilo HomeMap) -->
        <div class="map-icons cemeteries-map-icons">
            <a-button
                v-for="item in cemeteryMenuItems"
                :key="item.id"
                class="map-icon-btn"
                :title="item.name"
                :aria-label="item.name"
                @click="openMenuRouteModal(item)"
            >
                <template #icon>
                    <component
                        v-if="item.iconComponent"
                        :is="item.iconComponent"
                        class="menu-icon cemeteries-submenu-antd-icon"
                    />
                    <i
                        v-else
                        :class="['fas', item.faIcon, 'menu-icon']"
                        aria-hidden="true"
                    />
                </template>
            </a-button>
        </div>

        <!-- Modal exibindo o componente cadastrado na rota do menu -->
        <a-modal
            v-model:open="menuRouteModalVisible"
            :title="menuRouteModalData.name"
            :footer="null"
            width="90%"
            wrap-class-name="menu-route-modal-wrap"
            :body-style="{ position: 'relative', maxHeight: '75vh', overflow: 'hidden', padding: 0 }"
        >
            <div class="menu-route-modal-scroll">
                <template v-if="menuRouteModalData.componentName">
                    <div class="menu-route-component-wrapper">
                        <component :is="menuRouteModalData.componentName" />
                    </div>
                </template>
                <template v-else>
                    <a-descriptions bordered size="small" :column="1">
                        <a-descriptions-item label="Menu">{{ menuRouteModalData.name }}</a-descriptions-item>
                        <a-descriptions-item label="Rota cadastrada">
                            {{ menuRouteModalData.route || '—' }}
                        </a-descriptions-item>
                    </a-descriptions>
                    <p v-if="!menuRouteModalData.route" class="menu-route-no-component">
                        Nenhum componente cadastrado para esta rota.
                    </p>
                </template>
            </div>
            <a-tooltip
                v-if="menuRouteModalData.href"
                :title="menuRouteModalData.componentName ? 'Abrir em página completa' : 'Abrir rota'"
                placement="left"
            >
                <a-button
                    type="primary"
                    shape="circle"
                    class="menu-route-float-btn"
                    @click="goToMenuRoute(menuRouteModalData.href)"
                >
                    <template #icon>
                        <ExportOutlined />
                    </template>
                </a-button>
            </a-tooltip>
        </a-modal>

        <!-- Painel expansível: lista de cemitérios (canto inferior esquerdo, mesmo estilo dos ícones) -->
        <div class="cemeteries-panel" :class="{ expanded: panelExpanded }">
            <a-button
                type="button"
                class="panel-toggle map-icon-btn"
                :title="panelExpanded ? 'Recolher lista' : 'Ver cemitérios'"
                aria-label="Lista de cemitérios"
                @click="panelExpanded = !panelExpanded"
            >
                <template #icon>
                    <i class="fas fa-list menu-icon" aria-hidden="true"></i>
                </template>
            </a-button>
            <div v-show="panelExpanded" class="panel-content">
                <div class="panel-header">
                    <span>Cemitérios</span>
                </div>
                <div v-if="loadingCemeteries" class="panel-loading">
                    <i class="fas fa-spinner fa-spin"></i> Carregando...
                </div>
                <ul v-else-if="cemeteries.length" class="cemeteries-list">
                    <li
                        v-for="item in cemeteries"
                        :key="item.gid"
                        class="cemetery-item"
                        @click="zoomToCemetery(item)"
                    >
                        <strong class="cemetery-name">{{ item.name }}</strong>
                        <span v-if="item.address" class="cemetery-address">{{ item.address }}</span>
                        <span v-else class="cemetery-address no-address">—</span>
                    </li>
                </ul>
                <div v-else class="panel-empty">Nenhum cemitério cadastrado.</div>
            </div>
        </div>

        <!-- Modal cadastro/visualização do cemitério -->
        <a-modal
            v-model:open="modalVisible"
            :title="modalTitle"
            :footer="null"
            width="480px"
            destroy-on-close
            @cancel="closeCemeteryModal"
        >
            <a-spin :spinning="modalLoading">
                <template v-if="!modalLoading && modalData">
                    <!-- Modo visualização (dados já cadastrados) -->
                    <div v-if="modalData.cemetery && !isEditMode" class="cemetery-modal-view">
                        <a-table
                            :columns="cemeteryViewColumns"
                            :data-source="cemeteryViewData"
                            :pagination="false"
                            size="small"
                            bordered
                            :row-key="(r) => r.key"
                        />
                        <div v-if="isModoCadastro" class="cemetery-modal-actions">
                            <a-button type="primary" @click="isEditMode = true">
                                <i class="fas fa-edit"></i> Editar
                            </a-button>
                        </div>
                    </div>
                    <!-- Modo edição ou cadastro (apenas se tiver permissão Modo Cadastro) -->
                    <div v-else-if="isModoCadastro" class="cemetery-modal-form">
                        <a-form layout="vertical" :model="formState">
                            <a-form-item label="Nome" name="description">
                                <a-input
                                    v-model:value="formState.description"
                                    placeholder="Nome do cemitério"
                                    allow-clear
                                />
                            </a-form-item>
                            <a-form-item label="Ativo" name="active">
                                <a-switch v-model:checked="formState.active" />
                            </a-form-item>
                            <a-form-item v-if="modalData.cemetery && isEditMode">
                                <a-space>
                                    <a-button @click="cancelEdit">Cancelar</a-button>
                                    <a-button type="primary" :loading="formSaving" @click="saveCemetery">
                                        Salvar
                                    </a-button>
                                </a-space>
                            </a-form-item>
                            <a-form-item v-else-if="!modalData.cemetery">
                                <a-button type="primary" :loading="formSaving" @click="createCemetery">
                                    Cadastrar
                                </a-button>
                            </a-form-item>
                        </a-form>
                    </div>
                    <!-- Modo consulta: sem permissão para cadastrar -->
                    <div v-else class="cemetery-modal-view">
                        <a-table
                            v-if="modalData.cemetery"
                            :columns="cemeteryViewColumns"
                            :data-source="cemeteryViewData"
                            :pagination="false"
                            size="small"
                            bordered
                            :row-key="(r) => r.key"
                        />
                        <p v-else class="cemetery-modal-consulta-only">
                            Apenas visualização. Você não tem permissão para cadastrar cemitérios.
                        </p>
                    </div>
                </template>
            </a-spin>
        </a-modal>

        <!-- Modal cadastro/visualização da sepultura (aberto pelo clique na geometria) -->
        <a-modal
            v-model:open="modalSepultureVisible"
            :title="modalSepultureTitle"
            :footer="null"
            width="90%"
            style="max-width: 1200px"
            destroy-on-close
            :z-index="1000"
            wrap-class-name="sepulture-modal-wrap"
            :body-style="{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', overflowX: 'hidden' }"
            @cancel="closeSepultureModal"
        >
            <a-spin :spinning="modalSepultureLoading">
                <template v-if="!modalSepultureLoading && modalSepultureData !== null">
                    <!-- Modo visualização (dados já cadastrados) -->
                    <div v-if="modalSepultureData.sepulture && !isEditModeSepulture" class="sepulture-modal-view sepulture-modal-scroll">
                        <a-table
                            :columns="sepultureViewColumns"
                            :data-source="sepultureViewData"
                            :pagination="false"
                            size="small"
                            bordered
                            :row-key="(r) => r.key"
                            class="sepulture-view-table"
                        />
                        <div v-if="isModoCadastro" class="sepulture-modal-actions">
                            <a-tooltip title="Editar" :get-popup-container="(n) => n.closest('.ant-modal-content') || document.body">
                                <span class="sepulture-action-trigger" title="Editar">
                                    <a-button type="primary" size="middle" class="sepulture-action-btn" aria-label="Editar" @click="isEditModeSepulture = true">
                                        <i class="fas fa-edit" aria-hidden="true"></i>
                                    </a-button>
                                </span>
                            </a-tooltip>
                        </div>
                    </div>
                    <!-- Modo edição ou cadastro (apenas se tiver permissão Modo Cadastro) -->
                    <div v-else-if="isModoCadastro" class="sepulture-modal-form sepulture-modal-scroll">
                        <a-form layout="vertical" :model="formStateSepulture" class="sepulture-form-fields">
                            <div class="sepulture-form-section">
                                <div class="sepulture-form-section-title">Localização</div>
                                <a-row :gutter="16">
                                    <a-col :span="12">
                                        <a-form-item label="Tipo de Sepultura" required>
                                            <a-select
                                                v-model:value="formStateSepulture.sepulture_type_code"
                                                placeholder="Selecione o tipo"
                                                :options="sepultureTypeOptions"
                                                :field-names="{ label: 'name', value: 'id' }"
                                                allow-clear
                                            />
                                        </a-form-item>
                                    </a-col>
                                </a-row>
                                <a-row :gutter="16">
                                    <a-col :span="12">
                                        <a-form-item label="Quadra">
                                            <a-input v-model:value="formStateSepulture.block_location" placeholder="Ex.: Quadra A (opcional)" />
                                        </a-form-item>
                                    </a-col>
                                    <a-col :span="12">
                                        <a-form-item label="Lote">
                                            <a-input v-model:value="formStateSepulture.lot_location" placeholder="Ex.: Lote 15 (opcional)" />
                                        </a-form-item>
                                    </a-col>
                                </a-row>
                            </div>
                            <div class="sepulture-form-section">
                                <div class="sepulture-form-section-title">Capacidade</div>
                                <a-row :gutter="16">
                                    <a-col :span="12">
                                        <a-form-item label="Quantidade máxima de sepultados" required>
                                            <a-input-number v-model:value="formStateSepulture.sepultures_quantity" :min="1" style="width: 100%" />
                                        </a-form-item>
                                    </a-col>
                                </a-row>
                            </div>
                            <div class="sepulture-form-section">
                                <a-form-item label="Descrição">
                                    <a-textarea v-model:value="formStateSepulture.description" placeholder="Descrição (opcional)" :rows="3" />
                                </a-form-item>
                            </div>
                            <a-row class="sepulture-form-actions">
                                <a-col :span="24">
                                    <a-form-item v-if="modalSepultureData.sepulture && isEditModeSepulture">
                                        <a-space>
                                            <a-button @click="cancelEditSepulture">Cancelar</a-button>
                                            <a-button type="primary" :loading="formSavingSepulture" @click="saveSepulture">
                                                Salvar
                                            </a-button>
                                        </a-space>
                                    </a-form-item>
                                    <a-form-item v-else-if="!modalSepultureData.sepulture">
                                        <a-button type="primary" :loading="formSavingSepulture" @click="createSepulture">
                                            Cadastrar
                                        </a-button>
                                    </a-form-item>
                                </a-col>
                            </a-row>
                        </a-form>
                    </div>
                    <!-- Modo consulta: sem permissão para cadastrar -->
                    <div v-else class="sepulture-modal-view sepulture-modal-scroll">
                        <a-table
                            v-if="modalSepultureData.sepulture"
                            :columns="sepultureViewColumns"
                            :data-source="sepultureViewData"
                            :pagination="false"
                            size="small"
                            bordered
                            :row-key="(r) => r.key"
                            class="sepulture-view-table"
                        />
                        <p v-else class="cemetery-modal-consulta-only">
                            Apenas visualização. Você não tem permissão para cadastrar sepulturas.
                        </p>
                    </div>

                    <!-- Sepultamentos (quando há sepultura cadastrada) -->
                    <div v-if="modalSepultureData?.sepulture?.id" class="sepulture-burials-section">
                        <div class="sepulture-form-section-title">
                            Sepultamentos
                            <a-tooltip v-if="isModoCadastro" :title="sepultureBurialLimitReached ? 'Limite máximo de sepultamentos já atingido.' : 'Incluir sepultamento'" :get-popup-container="(n) => n.closest('.ant-modal-content') || document.body">
                                <span class="sepulture-action-trigger" :title="sepultureBurialLimitReached ? 'Limite máximo de sepultamentos já atingido.' : 'Incluir sepultamento'">
                                    <a-button
                                        type="primary"
                                        size="small"
                                        class="add-burial-btn sepulture-action-btn"
                                        aria-label="Incluir sepultamento"
                                        :disabled="sepultureBurialLimitReached"
                                        @click="openBurialModal()"
                                    >
                                        <i class="fas fa-plus" aria-hidden="true"></i>
                                    </a-button>
                                </span>
                            </a-tooltip>
                        </div>
                        <a-table
                            :columns="burialsTableColumns"
                            :data-source="burialsList"
                            :loading="burialsLoading"
                            :pagination="false"
                            size="small"
                            bordered
                            row-key="id"
                        >
                            <template #bodyCell="{ column, record }">
                                <template v-if="column.key === 'action'">
                                    <span class="burials-table-actions">
                                        <a-tooltip title="Editar" :get-popup-container="(n) => n.closest('.ant-modal-content') || document.body">
                                            <span class="burial-action-trigger" title="Editar">
                                                <a-button type="text" size="small" class="burial-action-btn" aria-label="Editar" @click="openBurialModal(record)">
                                                    <i class="fas fa-edit" aria-hidden="true"></i>
                                                </a-button>
                                            </span>
                                        </a-tooltip>
                                        <a-tooltip title="Visualizar anexos" :get-popup-container="(n) => n.closest('.ant-modal-content') || document.body">
                                            <span class="burial-action-trigger" title="Visualizar anexos">
                                                <a-button type="text" size="small" class="burial-action-btn" aria-label="Visualizar anexos" @click="openArchivesViewModal(record)">
                                                    <i class="fas fa-paperclip" aria-hidden="true"></i>
                                                </a-button>
                                            </span>
                                        </a-tooltip>
                                        <a-tooltip title="Excluir" :get-popup-container="(n) => n.closest('.ant-modal-content') || document.body">
                                            <span class="burial-action-trigger" title="Excluir">
                                                <a-button type="text" danger size="small" class="burial-action-btn" aria-label="Excluir" @click="confirmDeleteBurial(record)">
                                                    <i class="fas fa-trash-alt" aria-hidden="true"></i>
                                                </a-button>
                                            </span>
                                        </a-tooltip>
                                    </span>
                                </template>
                            </template>
                        </a-table>
                    </div>
                </template>
            </a-spin>
        </a-modal>

        <!-- Modal Incluir/Editar Sepultamento -->
        <a-modal
            v-model:open="burialModalVisible"
            :title="burialModalEditingId ? 'Editar sepultamento' : 'Incluir sepultamento'"
            :footer="null"
            width="640px"
            destroy-on-close
            :z-index="1100"
            wrap-class-name="burial-modal-wrap"
            :body-style="{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', overflowX: 'hidden' }"
            @cancel="closeBurialModal"
        >
            <a-form layout="vertical" :model="burialFormState">
                <a-row :gutter="16">
                    <a-col :span="24">
                        <a-form-item label="Causa mortis">
                            <a-textarea v-model:value="burialFormState.causa_mortis" placeholder="Causa mortis (opcional)" :rows="2" />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="Data do sepultamento">
                            <a-date-picker
                                v-model:value="burialFormState.burial_date"
                                format="DD/MM/YYYY"
                                value-format="YYYY-MM-DD"
                                style="width: 100%"
                                placeholder="Selecione a data"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="Nome ou CPF" required>
                            <a-select
                                key="burial-defunct"
                                v-model:value="burialDefunctId"
                                placeholder="Pesquise por nome ou CPF"
                                :options="physicalPeopleDefunctOptions"
                                :field-names="{ label: 'name', value: 'id' }"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                show-search
                                :filter-option="false"
                                allow-clear
                                :loading="physicalPeopleLoading"
                                @search="onPhysicalPeopleSearch"
                                @dropdown-visible-change="(open) => open && !physicalPeopleList.length && loadPhysicalPeople()"
                            >
                                <template #notFoundContent>
                                    <div class="select-not-found" @mousedown.stop>
                                        Nenhum resultado encontrado.
                                        <a href="javascript:void(0)" @click.prevent="openCadastroModal('physical')">Cadastrar pessoa física</a>
                                    </div>
                                </template>
                            </a-select>
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="Responsável" required>
                            <a-select
                                key="burial-responsible"
                                v-model:value="burialResponsibleId"
                                placeholder="Pesquise por nome ou CPF"
                                :options="physicalPeopleOptions"
                                :field-names="{ label: 'name', value: 'id' }"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                show-search
                                :filter-option="false"
                                allow-clear
                                :loading="physicalPeopleLoading"
                                @search="onPhysicalPeopleSearch"
                                @dropdown-visible-change="(open) => open && !physicalPeopleList.length && loadPhysicalPeople()"
                            >
                                <template #notFoundContent>
                                    <div class="select-not-found" @mousedown.stop>
                                        Nenhum resultado encontrado.
                                        <a href="javascript:void(0)" @click.prevent="openCadastroModal('physical')">Cadastrar pessoa física</a>
                                    </div>
                                </template>
                            </a-select>
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="Legista" required>
                            <a-select
                                key="burial-legist"
                                v-model:value="burialLegistId"
                                placeholder="Pesquise por nome ou CPF"
                                :options="physicalPeopleOptions"
                                :field-names="{ label: 'name', value: 'id' }"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                show-search
                                :filter-option="false"
                                allow-clear
                                :loading="physicalPeopleLoading"
                                @search="onPhysicalPeopleSearch"
                                @dropdown-visible-change="(open) => open && !physicalPeopleList.length && loadPhysicalPeople()"
                            >
                                <template #notFoundContent>
                                    <div class="select-not-found" @mousedown.stop>
                                        Nenhum resultado encontrado.
                                        <a href="javascript:void(0)" @click.prevent="openCadastroModal('physical')">Cadastrar pessoa física</a>
                                    </div>
                                </template>
                            </a-select>
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="Hospital">
                            <a-select
                                key="burial-hospital"
                                v-model:value="burialHospitalId"
                                placeholder="Pesquise por razão social ou CNPJ"
                                :options="legalPeopleOptions"
                                :field-names="{ label: 'company_name', value: 'id' }"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                show-search
                                :filter-option="false"
                                allow-clear
                                :loading="legalPeopleLoading"
                                @search="onLegalPeopleSearch"
                                @dropdown-visible-change="(open) => open && !legalPeopleList.length && loadLegalPeople()"
                            >
                                <template #notFoundContent>
                                    <div class="select-not-found" @mousedown.stop>
                                        Nenhum resultado encontrado.
                                        <a href="javascript:void(0)" @click.prevent="openCadastroModal('legal')">Cadastrar pessoa jurídica</a>
                                    </div>
                                </template>
                            </a-select>
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="Funerária">
                            <a-select
                                key="burial-funerary"
                                v-model:value="burialFuneraryId"
                                placeholder="Pesquise por razão social ou CNPJ"
                                :options="legalPeopleOptions"
                                :field-names="{ label: 'company_name', value: 'id' }"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                show-search
                                :filter-option="false"
                                allow-clear
                                :loading="legalPeopleLoading"
                                @search="onLegalPeopleSearch"
                                @dropdown-visible-change="(open) => open && !legalPeopleList.length && loadLegalPeople()"
                            >
                                <template #notFoundContent>
                                    <div class="select-not-found" @mousedown.stop>
                                        Nenhum resultado encontrado.
                                        <a href="javascript:void(0)" @click.prevent="openCadastroModal('legal')">Cadastrar pessoa jurídica</a>
                                    </div>
                                </template>
                            </a-select>
                        </a-form-item>
                    </a-col>
                </a-row>
                <!-- Anexos: listar existentes (ao editar) + anexos a incluir ao salvar -->
                <a-row>
                    <a-col :span="24">
                        <div class="sepulture-form-section" style="margin-top: 16px;">
                            <div class="sepulture-form-section-title">Anexos</div>
                            <template v-if="burialModalEditingId">
                                <a-spin :spinning="archivesLoading">
                                    <div v-if="archivesList.length" class="burial-archives-list">
                                        <div
                                            v-for="item in archivesList"
                                            :key="item.id"
                                            class="burial-archive-item"
                                        >
                                            <div class="burial-archive-preview" @click="openArchiveUrl(item.url)">
                                                <img
                                                    v-if="item.archive_type === 'photo' || isImagePath(item.path)"
                                                    :src="item.url"
                                                    :alt="item.archive_type"
                                                    class="burial-archive-thumb"
                                                    @error="($e) => $e.target.style.display = 'none'"
                                                />
                                                <div v-else class="burial-archive-icon">
                                                    <i class="fas fa-file-alt"></i>
                                                </div>
                                            </div>
                                            <div class="burial-archive-meta">
                                                <span class="burial-archive-type-label">{{ archiveTypeLabel(item.archive_type) }}</span>
                                                <span class="burial-archive-actions">
                                                    <a-tooltip
                                                        title="Abrir em nova aba"
                                                        :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                                    >
                                                        <span class="burial-archive-tooltip-trigger" title="Abrir em nova aba">
                                                            <a-button
                                                                type="text"
                                                                size="small"
                                                                class="burial-archive-btn-icon"
                                                                aria-label="Abrir em nova aba"
                                                                @click="openArchiveUrl(item.url)"
                                                            >
                                                                <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                                                            </a-button>
                                                        </span>
                                                    </a-tooltip>
                                                    <a-tooltip
                                                        title="Excluir anexo"
                                                        :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                                    >
                                                        <span class="burial-archive-tooltip-trigger" title="Excluir anexo">
                                                            <a-button
                                                                type="text"
                                                                danger
                                                                size="small"
                                                                class="burial-archive-btn-icon"
                                                                aria-label="Excluir anexo"
                                                                @click="confirmDeleteArchive(item)"
                                                            >
                                                                <i class="fas fa-trash-alt" aria-hidden="true"></i>
                                                            </a-button>
                                                        </span>
                                                    </a-tooltip>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="burial-archives-empty">Nenhum anexo salvo.</div>
                                </a-spin>
                            </template>
                            <div class="burial-archive-upload">
                                <div class="burial-pending-label">Anexos a incluir ao salvar:</div>
                                <ul v-if="pendingArchives.length" class="burial-pending-list">
                                    <li v-for="(item, index) in pendingArchives" :key="index" class="burial-pending-item">
                                        <span>{{ item.name }}</span>
                                        <span class="burial-pending-type">({{ archiveTypeLabel(item.archive_type) }})</span>
                                        <a-button type="link" danger size="small" @click="removePendingArchive(index)">Remover</a-button>
                                    </li>
                                </ul>
                                <div v-else class="burial-archives-empty">Nenhum arquivo na lista. Selecione o tipo e clique em &quot;Selecionar arquivo&quot; abaixo.</div>
                                <div class="burial-archive-upload-row">
                                    <label class="burial-archive-upload-label">Tipo de anexo</label>
                                    <a-select
                                        v-model:value="newArchiveType"
                                        placeholder="Selecione o tipo"
                                        class="burial-archive-type-select"
                                        :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                                    >
                                        <a-select-option value="photo">Foto</a-select-option>
                                        <a-select-option value="death_certificate">Certidão de óbito</a-select-option>
                                        <a-select-option value="other">Outro</a-select-option>
                                    </a-select>
                                    <input
                                        ref="archiveFileInputRef"
                                        type="file"
                                        :accept="archiveAcceptByType"
                                        class="burial-archive-file-input"
                                        @change="onArchiveFileChange"
                                    />
                                    <a-button @click="triggerArchiveFileInput">Selecionar arquivo</a-button>
                                </div>
                            </div>
                        </div>
                    </a-col>
                </a-row>
                <a-form-item>
                    <a-space>
                        <a-button @click="closeBurialModal">Cancelar</a-button>
                        <a-button type="primary" :loading="burialSaving" @click="submitBurial">
                            {{ burialModalEditingId ? 'Salvar' : 'Incluir' }}
                        </a-button>
                    </a-space>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- Modal Visualizar anexos do sepultamento -->
        <a-modal
            v-model:open="archivesViewModalVisible"
            title="Anexos do sepultamento"
            :footer="null"
            width="85%"
            style="max-width: 900px"
            :z-index="1100"
            wrap-class-name="archives-view-modal-wrap"
            @cancel="closeArchivesViewModal"
        >
            <a-spin :spinning="archivesViewLoading">
                <div v-if="archivesViewList.length" class="archives-view-carousel-wrap">
                    <div class="archives-view-carousel-inner">
                        <a-button
                            v-if="archivesViewList.length > 1"
                            type="text"
                            class="archives-view-arrow archives-view-arrow-prev"
                            aria-label="Anterior"
                            @click="goToPrevArchivesSlide"
                        >
                            <i class="fas fa-chevron-left" aria-hidden="true"></i>
                        </a-button>
                        <a-carousel ref="archivesCarouselRef" effect="fade" class="archives-view-carousel">
                            <div v-for="(item, index) in archivesViewList" :key="item.id" class="archives-view-slide">
                            <div class="archives-view-slide-inner">
                                <template v-if="item.archive_type === 'photo' || isImagePath(item.path)">
                                    <img :src="item.url" :alt="'Anexo ' + (index + 1)" class="archives-view-img" @error="($e) => $e.target.style.display = 'none'" />
                                </template>
                                <template v-else-if="isPdfPath(item.path)">
                                    <embed v-if="item.url" :src="item.url" type="application/pdf" class="archives-view-pdf" />
                                </template>
                                <template v-else>
                                    <div class="archives-view-file-card">
                                        <i class="fas fa-file-alt archives-view-file-icon"></i>
                                        <span class="archives-view-file-type">{{ archiveTypeLabel(item.archive_type) }}</span>
                                        <a-button type="primary" size="small" @click="openArchiveUrl(item.url)">
                                            <i class="fas fa-external-link-alt"></i> Abrir em nova aba
                                        </a-button>
                                    </div>
                                </template>
                            </div>
                            <div class="archives-view-slide-caption">{{ archiveTypeLabel(item.archive_type) }} ({{ index + 1 }}/{{ archivesViewList.length }})</div>
                        </div>
                        </a-carousel>
                        <a-button
                            v-if="archivesViewList.length > 1"
                            type="text"
                            class="archives-view-arrow archives-view-arrow-next"
                            aria-label="Próximo"
                            @click="goToNextArchivesSlide"
                        >
                            <i class="fas fa-chevron-right" aria-hidden="true"></i>
                        </a-button>
                    </div>
                </div>
                <div v-else class="archives-view-empty">Nenhum anexo cadastrado para este sepultamento.</div>
            </a-spin>
            <div class="archives-view-modal-footer">
                <a-button @click="closeArchivesViewModal">Fechar</a-button>
            </div>
        </a-modal>

        <!-- Modal Cadastro Pessoa Física / Jurídica (z-index alto para ficar sempre acima) -->
        <a-modal
            v-model:open="cadastroModalVisible"
            :title="cadastroModalType === 'physical' ? 'Cadastrar pessoa física' : 'Cadastrar pessoa jurídica'"
            :footer="null"
            width="90%"
            :z-index="9999"
            wrap-class-name="cadastro-person-modal-wrap"
            :body-style="{ maxHeight: '75vh', overflow: 'auto', padding: '16px' }"
            @cancel="closeCadastroModal"
        >
            <div v-if="cadastroModalVisible" class="cadastro-person-component-wrapper">
                <component :is="cadastroComponentName" />
            </div>
            <div class="cadastro-person-modal-footer">
                <a-button type="primary" @click="closeCadastroModal">Fechar e atualizar lista</a-button>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { onMounted, ref, computed, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'

import OlMap from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import { defaults as defaultControls } from 'ol/control'
import Swipe from 'ol-ext/control/Swipe'
import GeoJSON from 'ol/format/GeoJSON'
import { scaleLine } from '../../../open-layers/default-controls.js'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Style from 'ol/style/Style'
import Stroke from 'ol/style/Stroke'
import Fill from 'ol/style/Fill'
import Icon from 'ol/style/Icon'
import Text from 'ol/style/Text'
import Point from 'ol/geom/Point'
import { getCenter } from 'ol/extent'
import { SearchOutlined, ExportOutlined } from '@ant-design/icons-vue'
import { antdIconsMap } from '../../../utils/antdIcons'
import { getRegisteredLayers } from '../../../open-layers/map-helpers/home.js'

import { getDefaultMapView } from '../../../open-layers/map-defaults.js'
import MapLayerSwitcher from "../MapsOpenLayers/Components/MapLayerSwitcher.vue";

// ---------- Camadas: esquerda = Cemitério, direita = Sepultura (do cemitério selecionado) ----------
const leftLayerDefs = ref([])
const rightLayerDefs = ref([])
const selectedCemeteryGid = ref(null)
const minHomeIconSize = 16
const maxHomeIconSize = 48
const homeIconSize = ref(32)
const cemeteriesMapContainerStyle = computed(() => ({
    '--home-map-icon-size': `${homeIconSize.value}px`,
    '--home-map-icon-glyph-size': `${Math.round(homeIconSize.value * 0.5)}px`,
    '--home-map-icon-radius': `${Math.max(4, Math.round((homeIconSize.value / 32) * 6))}px`,
}))
const sepultureIconScaleFactor = computed(() => homeIconSize.value / 32)
const sepultureStyleCache = new Map()
let swipeRefreshTimer = null

// Consulta no mapa (falecido/responsável por nome ou CPF)
const consultaSearchTerm = ref('')
const consultaResults = ref([])
const consultaSearching = ref(false)
const showConsultaSearch = ref(false)
let consultaSearchTimeout = null

// ---------- Submenus do menu Cemitérios (ícones superior esquerdo) ----------
const cemeterySubmenus = ref([])
/** Menu pai "Cemitérios" (mesma lógica do VueMenu: ícone do filho ou do pai). */
const cemeteryParentMenu = ref(null)

function resolveCemeterySubmenuIconComponent(child) {
    if (child.icon && antdIconsMap[child.icon]) {
        return antdIconsMap[child.icon]
    }
    const p = cemeteryParentMenu.value
    if (p?.icon && antdIconsMap[p.icon]) {
        return antdIconsMap[p.icon]
    }
    return null
}

const ICON_BY_ROUTE = {
    cemeteries: 'fa-map-marked-alt',
    'cemeteries/sepulture-types': 'fa-tags',
    'cemeteries/tipos-sepultura': 'fa-tags',
}
const ICON_BY_NAME = {
    'Tipos de Sepultura': 'fa-tags',
    Mapa: 'fa-map-marked-alt',
    Cemitérios: 'fa-map-marked-alt',
}

// Itens com rota (exclui "Modo Cadastro", que não tem ícone e só define permissão)
const cemeteryMenuItems = computed(() => {
    const items = []
    const subs = (cemeterySubmenus.value || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))
    subs.forEach((child) => {
        const route = child.route || ''
        if (!route) return
        const href = `/${route}`
        const iconComponent = resolveCemeterySubmenuIconComponent(child)
        const faIcon =
            iconComponent == null
                ? ICON_BY_ROUTE[route] || ICON_BY_NAME[child.name] || 'fa-folder'
                : null
        items.push({
            id: child.id,
            name: child.name,
            route,
            href,
            iconComponent,
            faIcon,
        })
    })
    return items
})

// Modo Cadastro: se o usuário tem permissão, pode cadastrar/editar cemitério e sepulturas; senão, modo consulta (só visualização)
const isModoCadastro = ref(false)

// Mapa rota -> nome do componente Vue (registrado globalmente no app)
const ROUTE_TO_COMPONENT = {
    'cemeteries/sepulture-types': 'sepulture-types',
}

const menuRouteModalVisible = ref(false)
const menuRouteModalData = ref({ name: '', route: '', href: '', componentName: '' })

function openMenuRouteModal(item) {
    const route = item.route || ''
    menuRouteModalData.value = {
        name: item.name,
        route,
        href: item.href || '',
        componentName: ROUTE_TO_COMPONENT[route] || '',
    }
    menuRouteModalVisible.value = true
}

function goToMenuRoute(href) {
    if (href) window.location.href = href
}

// ---------- Painel de cemitérios ----------
const panelExpanded = ref(false)
const cemeteries = ref([])
const loadingCemeteries = ref(false)

// ---------- Modal cemitério ----------
const modalVisible = ref(false)
const modalLoading = ref(false)
const modalData = ref(null)
const isEditMode = ref(false)
const formSaving = ref(false)
const formState = ref({ description: '', active: true })

const modalTitle = computed(() => {
    if (!modalData.value) return 'Cemitério'
    if (modalData.value.cemetery && !isEditMode.value) return 'Dados do Cemitério'
    if (modalData.value.cemetery && isEditMode.value) return 'Editar Cemitério'
    return 'Cadastrar Cemitério'
})

const cemeteryViewColumns = [
    { title: 'Nome', dataIndex: 'description', key: 'description', width: 200 },
    { title: 'Ativo', dataIndex: 'active', key: 'active', width: 80 },
]
const cemeteryViewData = computed(() => {
    if (!modalData.value?.cemetery) return []
    const c = modalData.value.cemetery
    return [{ key: 'row', description: c.description || '—', active: c.active ? 'Sim' : 'Não' }]
})

// ---------- Modal sepultura (clique na geometria) ----------
const modalSepultureVisible = ref(false)
const modalSepultureLoading = ref(false)
const modalSepultureData = ref(null)
const isEditModeSepulture = ref(false)
const formSavingSepulture = ref(false)
const formStateSepulture = ref({
    description: '',
    block_location: '',
    lot_location: '',
    sepultures_quantity: 1,
    sepulture_type_code: undefined,
    cemetery_id: undefined,
})
const cemeteriesListForSepulture = ref([])
const sepultureTypesList = ref([])

const modalSepultureTitle = computed(() => {
    if (!modalSepultureData.value) return 'Sepultura'
    if (modalSepultureData.value.sepulture && !isEditModeSepulture.value) return 'Dados da Sepultura'
    if (modalSepultureData.value.sepulture && isEditModeSepulture.value) return 'Editar Sepultura'
    return 'Cadastrar Sepultura'
})

const sepultureViewColumns = [
    { title: 'Cemitério', dataIndex: 'cemetery', key: 'cemetery', ellipsis: true },
    { title: 'Tipo de Sepultura', dataIndex: 'sepulture_type', key: 'sepulture_type', ellipsis: true },
    { title: 'Quadra', dataIndex: 'block_location', key: 'block_location', ellipsis: true },
    { title: 'Lote', dataIndex: 'lot_location', key: 'lot_location', ellipsis: true },
    { title: 'Qtd. máx. sepultados', dataIndex: 'sepultures_quantity', key: 'sepultures_quantity', ellipsis: true },
    { title: 'Descrição', dataIndex: 'description', key: 'description', ellipsis: true },
]
const sepultureViewData = computed(() => {
    if (!modalSepultureData.value?.sepulture) return []
    const s = modalSepultureData.value.sepulture
    const sepultureType = s.sepultureType ?? s.sepulture_type
    const cemetery = s.cemetery
    return [{
        key: 'row',
        cemetery: cemetery?.description || '—',
        sepulture_type: sepultureType?.name || '—',
        block_location: s.block_location || '—',
        lot_location: s.lot_location || '—',
        sepultures_quantity: s.sepultures_quantity ?? '—',
        description: s.description || '—',
    }]
})

const cemeteryOptionsForSepulture = computed(() =>
    (cemeteriesListForSepulture.value || []).map((c) => ({
        id: c.id,
        description: c.description || 'Cemitério #' + c.id,
    }))
)
const sepultureTypeOptions = computed(() =>
    (sepultureTypesList.value || []).map((t) => ({ id: t.id, name: t.name || 'Tipo #' + t.id }))
)

// ---------- Sepultamentos (burials) ----------
const burialsList = ref([])
const burialsLoading = ref(false)
const sepultureBurialLimitReached = computed(() => {
    const limit = modalSepultureData.value?.sepulture?.sepultures_quantity
    if (limit == null || limit === '') return false
    return (burialsList.value?.length ?? 0) >= Number(limit)
})
const burialModalVisible = ref(false)
const burialModalEditingId = ref(null)
const burialSaving = ref(false)
const physicalPeopleList = ref([])
const legalPeopleList = ref([])
const physicalPeopleLoading = ref(false)
const legalPeopleLoading = ref(false)
let physicalPeopleSearchTimeout = null
let legalPeopleSearchTimeout = null

const burialFormState = ref({
    causa_mortis: '',
    burial_date: null,
})
// Refs separados para cada select de pessoa, evitando que um sobrescreva o outro
const burialDefunctId = ref(undefined)
const burialResponsibleId = ref(undefined)
const burialLegistId = ref(undefined)
const burialHospitalId = ref(undefined)
const burialFuneraryId = ref(undefined)

// Anexos do sepultamento (archives)
const archivesList = ref([])
const archivesLoading = ref(false)
const pendingArchives = ref([]) // { file: File, archive_type: string, name: string }
const newArchiveType = ref('other')
const archiveFileInputRef = ref(null)
const archivesCarouselRef = ref(null)
const archiveTypeOptions = [
    { value: 'photo', label: 'Foto' },
    { value: 'death_certificate', label: 'Certidão de óbito' },
    { value: 'other', label: 'Outro' },
]
const archiveAcceptByType = computed(() => {
    if (newArchiveType.value === 'photo') {
        return 'image/*,.jpg,.jpeg,.png,.gif,.webp,.bmp'
    }
    if (newArchiveType.value === 'death_certificate') {
        return '.pdf,.doc,.docx,.odt,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain'
    }
    return ''
})

// Modal visualizar anexos (carrossel)
const archivesViewModalVisible = ref(false)
const archivesViewList = ref([])
const archivesViewLoading = ref(false)
const mapRef = ref(null)
const layerSwitcherData = ref(null)

function formatDatePtBr(value) {
    if (!value) return '—'
    const d = typeof value === 'string' ? value.split('T')[0] : value
    const [y, m, day] = d.split('-')
    return day && m && y ? `${day}/${m}/${y}` : value
}

const burialsTableColumns = computed(() => {
    const base = [
        { title: 'Data', dataIndex: 'burial_date', key: 'burial_date', width: 110, customRender: ({ text }) => formatDatePtBr(text) },
        { title: 'Falecido', dataIndex: 'defunct', key: 'defunct', ellipsis: true },
        { title: 'Responsável', dataIndex: 'responsible', key: 'responsible', ellipsis: true },
        { title: 'Legista', dataIndex: 'legist', key: 'legist', ellipsis: true },
        { title: 'Causa mortis', dataIndex: 'causa_mortis', key: 'causa_mortis', ellipsis: true },
    ]
    if (isModoCadastro.value) base.push({ title: 'Ação', key: 'action', width: 120 })
    return base
})

const physicalPeopleOptions = computed(() =>
    (physicalPeopleList.value || []).map((p) => ({
        id: p.id,
        name: p.name ? `${p.name}${p.cpf ? ' (' + p.cpf + ')' : ''}` : 'Pessoa #' + p.id,
    }))
)

/** Falecido: mesmo rótulo nome/CPF; desabilita quem já sepultado (API ou mesma sepultura na lista). */
const physicalPeopleDefunctOptions = computed(() =>
    (physicalPeopleList.value || []).map((p) => ({
        id: p.id,
        name: p.name ? `${p.name}${p.cpf ? ' (' + p.cpf + ')' : ''}` : 'Pessoa #' + p.id,
        disabled: isDefunctAlreadyBuried(p.id),
    }))
)
const legalPeopleOptions = computed(() =>
    (legalPeopleList.value || []).map((l) => ({
        id: l.id,
        company_name: (l.company_name || 'Pessoa jurídica #' + l.id) + (l.cnpj ? ' (' + l.cnpj + ')' : ''),
    }))
)

function filterOption(input, option) {
    const label = (option?.label ?? option?.name ?? option?.company_name ?? '').toString().toLowerCase()
    return label.includes((input || '').toLowerCase())
}

const occupiedDefunctIds = ref(new Set())

function isDefunctAlreadyBuried(personId) {
    if (personId === undefined || personId === null || personId === '') return false
    if (occupiedDefunctIds.value.has(String(personId))) {
        return true
    }
    return (burialsList.value || []).some(
        (b) =>
            String(b.defunct_id) === String(personId)
            && (!burialModalEditingId.value || b.id !== burialModalEditingId.value)
    )
}

async function loadOccupiedDefunctIds(exceptBurialId = null) {
    try {
        const params = {}
        if (exceptBurialId != null && exceptBurialId !== '') {
            params.except_burial_id = exceptBurialId
        }
        const { data } = await window.axios.get('/api/cemeteries/sepultamento/occupied-defunct-ids', { params })
        const ids = Array.isArray(data?.data) ? data.data : []
        occupiedDefunctIds.value = new Set(ids.map((id) => String(id)))
    } catch (_) {
        occupiedDefunctIds.value = new Set()
    }
}

function onPhysicalPeopleSearch(term) {
    if (physicalPeopleSearchTimeout) clearTimeout(physicalPeopleSearchTimeout)
    physicalPeopleSearchTimeout = setTimeout(() => {
        loadPhysicalPeople(typeof term === 'string' ? term.trim() : '')
    }, 300)
}

function onLegalPeopleSearch(term) {
    if (legalPeopleSearchTimeout) clearTimeout(legalPeopleSearchTimeout)
    legalPeopleSearchTimeout = setTimeout(() => {
        loadLegalPeople(typeof term === 'string' ? term.trim() : '')
    }, 300)
}

const cadastroModalVisible = ref(false)
const cadastroModalType = ref('physical') // 'physical' | 'legal'
const cadastroComponentName = computed(() =>
    cadastroModalType.value === 'physical' ? 'physical-people' : 'legal-people'
)

function openCadastroModal(type) {
    cadastroModalType.value = type
    cadastroModalVisible.value = true
}

function closeCadastroModal() {
    cadastroModalVisible.value = false
    loadPhysicalPeople()
    loadLegalPeople()
}

let map
let swipe
let highlightLayer
const layerPool = new Map()

const GEOJSON_FORMAT = new GeoJSON()
// API retorna geometrias em WGS84 (4326); no banco estão em 31982 e o backend faz ST_Transform ao montar o GeoJSON
const WGS84 = 'EPSG:4326'
const WEB_MERCATOR = 'EPSG:3857'

const HIGHLIGHT_STYLE = new Style({
    fill: new Fill({ color: 'rgba(255, 0, 0, 0.08)' }),
    stroke: new Stroke({ color: '#c00', width: 3 }),
})

const CEMETERIES_LAYER_STYLE = new Style({
    fill: new Fill({ color: 'rgba(80, 80, 80, 0.2)' }),
    stroke: new Stroke({ color: '#333', width: 2 }),
})

// Geometrias com sepultura cadastrada: amarelo
const SEPULTURES_LAYER_STYLE = new Style({
    fill: new Fill({ color: 'rgba(255, 200, 0, 0.35)' }),
    stroke: new Stroke({ color: '#b8860b', width: 1.5 }),
})
// Geometrias sem sepultura cadastrada: borda vermelha
const SEPULTURES_NO_CADASTRO_STYLE = new Style({
    fill: new Fill({ color: 'rgba(0, 80, 120, 0.15)' }),
    stroke: new Stroke({ color: '#c00', width: 2 }),
})

/** Retorna geometria ponto no centro/interior da geometria da feature (para ícone) */
function getSepultureIconGeometry(feature) {
    const g = feature.getGeometry()
    if (!g) return null
    const type = g.getType()
    if (type === 'Polygon') return g.getInteriorPoint()
    if (type === 'MultiPolygon') {
        const pts = g.getInteriorPoints()
        const coords = pts.getCoordinates()
        return coords.length ? new Point(coords[0]) : new Point(getCenter(g.getExtent()))
    }
    return new Point(getCenter(g.getExtent()))
}

// ---------- Criar camada Cemitério (esquerda) ----------
function createCemeteriesLayer(items) {
    const source = new VectorSource()
    const format = new GeoJSON()
    ;(items || []).forEach((item) => {
        if (!item.geom_geojson) return
        try {
            const feature = format.readFeature(
                { type: 'Feature', geometry: item.geom_geojson },
                { dataProjection: WGS84, featureProjection: WEB_MERCATOR }
            )
            if (feature.getGeometry()) {
                feature.set('gid', item.gid)
                source.addFeature(feature)
            }
        } catch (_) {}
    })
    const layer = new VectorLayer({
        source,
        style: CEMETERIES_LAYER_STYLE,
        title: 'Cemitério',
        name: 'Cemitério',
        displayInLayerSwitcher: true,
    })
    return layer
}

// ---------- Criar camada Sepultura (direita; conteúdo atualizado ao selecionar cemitério) ----------
function createSepulturesLayer() {
    const source = new VectorSource()
    const layer = new VectorLayer({
        source,
        style: (feature, resolution) => {
            const baseStyle = feature.get('has_sepulture') ? SEPULTURES_LAYER_STYLE : SEPULTURES_NO_CADASTRO_STYLE
            const imageUrl = feature.get('sepulture_type_image_url')
            const label = feature.get('sepulture_label')
            const iconGeom = feature.get('_icon_geometry')

            if (imageUrl && iconGeom) {
                const personalizationFactor = sepultureIconScaleFactor.value
                const scale = Math.max(0.02, Math.min(0.2, (0.5 / resolution) * personalizationFactor))
                const normalizedScale = Math.round(scale * 1000) / 1000
                const styleKey = [
                    feature.get('has_sepulture') ? '1' : '0',
                    imageUrl,
                    label || '',
                    normalizedScale,
                ].join('|')

                if (!sepultureStyleCache.has(styleKey)) {
                    const styles = [
                        baseStyle,
                        new Style({
                            geometry: (ft) => ft.get('_icon_geometry'),
                            image: new Icon({
                                src: imageUrl,
                                scale: normalizedScale,
                                anchor: [0.5, 0.5],
                                anchorXUnits: 'fraction',
                                anchorYUnits: 'fraction',
                            }),
                        }),
                    ]

                    if (label) {
                        styles.push(new Style({
                            geometry: (ft) => ft.get('_icon_geometry'),
                            text: new Text({
                                text: label,
                                textAlign: 'center',
                                textBaseline: 'top',
                                offsetY: 14,
                                font: '12px sans-serif',
                                fill: new Fill({ color: '#333' }),
                                stroke: new Stroke({ color: '#fff', width: 2 }),
                            }),
                        }))
                    }

                    sepultureStyleCache.set(styleKey, styles)
                }

                return sepultureStyleCache.get(styleKey)
            }

            return baseStyle
        },
        title: 'Sepultura',
        name: 'Sepultura',
        displayInLayerSwitcher: true,
    })
    return layer
}

async function loadHomeIconPersonalization() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations')
        const value = Number(data?.data?.home_icon_size)
        if (Number.isFinite(value)) {
            homeIconSize.value = Math.min(maxHomeIconSize, Math.max(minHomeIconSize, value))
        }
    } catch (_) {
        // Mantém o tamanho padrão quando não houver personalização.
    }
}

async function loadSepulturesForCemetery(gidCemiterio) {
    const layer = layerPool.get('sepultura::right')
    if (!layer) return
    const source = layer.getSource()
    source.clear()
    if (!gidCemiterio) return
    try {
        const { data } = await window.axios.get('/api/cemeteries/geo-sepulture', {
            params: { gid_cemitery: gidCemiterio },
        })
        const list = data?.data ?? []
        const format = new GeoJSON()
        const featureCollection = {
            type: 'FeatureCollection',
            features: list
                .filter((item) => item?.geom_geojson)
                .map((item) => ({
                    type: 'Feature',
                    geometry: item.geom_geojson,
                    properties: {
                        gid: item.gid,
                        has_sepulture: item.has_sepulture === true,
                        sepulture_type_image_url: item.sepulture_type_image_url || null,
                        sepulture_label: item.sepulture_label || null,
                    },
                })),
        }

        const features = format.readFeatures(featureCollection, {
            dataProjection: WGS84,
            featureProjection: WEB_MERCATOR,
        })

        features.forEach((feature) => {
            feature.set('_icon_geometry', getSepultureIconGeometry(feature))
        })

        source.addFeatures(features)
    } catch (_) {
        source.clear()
    }
}

function getCachedOrCreate(cacheKey, factory) {
    if (layerPool.has(cacheKey)) return layerPool.get(cacheKey)
    const layer = factory()
    layerPool.set(cacheKey, layer)
    return layer
}

function ensureInMap(layer) {
    if (!layer) return
    const layers = map.getLayers().getArray()
    if (!layers.includes(layer)) map.addLayer(layer)
}

function removeFromSwipe(layer) {
    if (!layer || !swipe) return
    try { swipe.removeLayer(layer) } catch {}
}

function removeAllFromSwipeAndMap() {
    layerPool.forEach(l => {
        removeFromSwipe(l)
        try { map.removeLayer(l) } catch {}
    })
}

async function applySwipeLayers() {
    if (!swipe) return
    removeAllFromSwipeAndMap()

    // A base fica sob responsabilidade do MapLayerSwitcher.
    // Aqui o Swipe controla apenas os overlays de cada lado.
    for (const def of leftLayerDefs.value) {
        const cacheKey = `${def.key}::left`
        const layer = layerPool.has(cacheKey)
            ? layerPool.get(cacheKey)
            : (typeof def.make === 'function' ? await def.make() : def.make)
        if (layer && !layerPool.has(cacheKey)) layerPool.set(cacheKey, layer)
        if (layer) {
            ensureInMap(layer)
            swipe.addLayer(layer)
        }
    }

    // Direita: apenas overlays
    for (const def of rightLayerDefs.value) {
        const cacheKey = `${def.key}::right`
        const layer = layerPool.has(cacheKey)
            ? layerPool.get(cacheKey)
            : (typeof def.make === 'function' ? await def.make() : def.make)
        if (layer && !layerPool.has(cacheKey)) layerPool.set(cacheKey, layer)
        if (layer) {
            ensureInMap(layer)
            swipe.addLayer(layer, true)
        }
    }

    map.render()
}

function queueSwipeRefresh() {
    if (swipeRefreshTimer !== null) {
        window.clearTimeout(swipeRefreshTimer)
    }

    swipeRefreshTimer = window.setTimeout(async () => {
        swipeRefreshTimer = null
        if (!map || !swipe) return
        await applySwipeLayers()
        map.render()
    }, 32)
}

function handleLayerSwitcherUpdate(data) {
    layerSwitcherData.value = {
        ...data,
        updateKey: Date.now(),
    }
    queueSwipeRefresh()
}

// ---------- Lista de cemitérios e zoom na geometria ----------
async function fetchCemeterySubmenus() {
    try {
        const { data } = await window.axios.get('/api/configurations/menus?active=true')
        const menus = data?.data ?? []
        const cemiterioMenu = menus.find((m) => m.route === 'cemeteries' || (m.name && m.name.includes('Cemitério')))
        cemeteryParentMenu.value = cemiterioMenu
            ? { id: cemiterioMenu.id, icon: cemiterioMenu.icon }
            : null
        cemeterySubmenus.value = cemiterioMenu?.childrens ?? []

        const modoCadastroMenu = (cemeterySubmenus.value || []).find(
            (c) => c.name && c.name.toLowerCase().includes('modo cadastro')
        )
        if (modoCadastroMenu && modoCadastroMenu.id) {
            try {
                const res = await window.axios.get(
                    `/api/configurations/menus/${modoCadastroMenu.id}/verify-permission-from-user`
                )
                isModoCadastro.value = !!res?.data?.data
            } catch (_) {
                isModoCadastro.value = false
            }
        } else {
            isModoCadastro.value = false
        }
    } catch (_) {
        cemeterySubmenus.value = []
        cemeteryParentMenu.value = null
        isModoCadastro.value = false
    }
}

async function fetchCemeteries() {
    loadingCemeteries.value = true
    try {
        const { data } = await window.axios.get('/api/cemeteries/geo-cemetery')
        cemeteries.value = data?.data ?? []
    } catch (e) {
        cemeteries.value = []
    } finally {
        loadingCemeteries.value = false
    }
}

function zoomToCemetery(item) {
    if (!map || !item?.geom_geojson) return
    selectedCemeteryGid.value = item.gid
    loadSepulturesForCemetery(item.gid)
    try {
        const feature = GEOJSON_FORMAT.readFeature(
            { type: 'Feature', geometry: item.geom_geojson },
            { dataProjection: WGS84, featureProjection: WEB_MERCATOR }
        )
        const geometry = feature.getGeometry()
        if (!geometry) return

        // Contorno vermelho na geometria
        if (!highlightLayer) {
            highlightLayer = new VectorLayer({
                source: new VectorSource(),
                style: HIGHLIGHT_STYLE,
                zIndex: 1000,
            })
            map.addLayer(highlightLayer)
        }
        highlightLayer.getSource().clear()
        highlightLayer.getSource().addFeature(new Feature(geometry.clone()))

        const extent = geometry.getExtent()
        map.getView().fit(extent, {
            padding: [24, 24, 24, 24],
            maxZoom: 20,
            duration: 500,
        })
    } catch (e) {
        console.warn('zoomToCemetery:', e)
    }
}

function onConsultaSearchInput() {
    if (consultaSearchTimeout) clearTimeout(consultaSearchTimeout)
    const term = (consultaSearchTerm.value || '').trim()
    if (term.length < 2) {
        consultaResults.value = []
        return
    }
    consultaSearchTimeout = setTimeout(async () => {
        consultaSearching.value = true
        consultaResults.value = []
        try {
            const { data } = await window.axios.get('/api/cemeteries/consulta', { params: { q: term } })
            consultaResults.value = Array.isArray(data?.data) ? data.data : []
        } catch (_) {
            consultaResults.value = []
        } finally {
            consultaSearching.value = false
        }
    }, 280)
}

function toggleConsultaSearch() {
    showConsultaSearch.value = !showConsultaSearch.value
    if (!showConsultaSearch.value) {
        consultaSearchTerm.value = ''
        consultaResults.value = []
        consultaSearching.value = false
        if (consultaSearchTimeout) clearTimeout(consultaSearchTimeout)
    }
}

function selectConsultaResult(item) {
    if (item?.gid) {
        zoomToConsultaSepulture(item)
        openSepultureModal(item.gid)
        consultaSearchTerm.value = ''
        consultaResults.value = []
    }
}

function zoomToConsultaSepulture(item) {
    if (!map || !item?.geom_geojson) return
    try {
        const feature = GEOJSON_FORMAT.readFeature(
            { type: 'Feature', geometry: item.geom_geojson },
            { dataProjection: WGS84, featureProjection: WEB_MERCATOR }
        )
        const geometry = feature.getGeometry()
        if (!geometry) return

        if (!highlightLayer) {
            highlightLayer = new VectorLayer({
                source: new VectorSource(),
                style: HIGHLIGHT_STYLE,
                zIndex: 1000,
            })
            map.addLayer(highlightLayer)
        }
        highlightLayer.getSource().clear()
        highlightLayer.getSource().addFeature(new Feature(geometry.clone()))

        map.getView().fit(geometry.getExtent(), {
            padding: [24, 24, 24, 24],
            maxZoom: 21,
            duration: 500,
        })
    } catch (_) {}
}

// ---------- Modal cemitério: abrir, fechar, salvar ----------
function getCemeteryLayer() {
    return layerPool.get('cemiterio::left')
}

function getSepultureLayer() {
    return layerPool.get('sepultura::right')
}

async function openCemeteryModal(gid) {
    modalVisible.value = true
    modalLoading.value = true
    modalData.value = null
    isEditMode.value = false
    formState.value = { description: '', active: true }
    try {
        const { data } = await window.axios.get(`/api/cemeteries/geo-cemetery/${gid}`)
        const payload = data?.data ?? null
        modalData.value = payload
        if (payload?.cemetery) {
            formState.value = {
                description: payload.cemetery.description ?? '',
                active: !!payload.cemetery.active,
            }
        }
    } catch (_) {
        modalData.value = { gid, cemetery: null }
    } finally {
        modalLoading.value = false
    }
}

function closeCemeteryModal() {
    modalVisible.value = false
    modalData.value = null
    isEditMode.value = false
}

function cancelEdit() {
    isEditMode.value = false
    if (modalData.value?.cemetery) {
        formState.value = {
            description: modalData.value.cemetery.description ?? '',
            active: !!modalData.value.cemetery.active,
        }
    }
}

async function saveCemetery() {
    if (!modalData.value?.cemetery?.id) return
    formSaving.value = true
    try {
        await window.axios.put(`/api/cemeteries/cemetery/${modalData.value.cemetery.id}`, {
            form: JSON.stringify(formState.value),
        })
        modalData.value = {
            ...modalData.value,
            cemetery: {
                ...modalData.value.cemetery,
                ...formState.value,
            },
        }
        isEditMode.value = false
        await fetchCemeteries()
        message.success('Cemitério atualizado.')
    } catch (_) {
        message.error('Erro ao salvar.')
    } finally {
        formSaving.value = false
    }
}

async function createCemetery() {
    if (!modalData.value?.gid) return
    formSaving.value = true
    try {
        const { data } = await window.axios.post('/api/cemeteries/cemetery', {
            description: formState.value.description || null,
            active: formState.value.active,
            geo_cemetery_gid: modalData.value.gid,
        })
        modalData.value = {
            ...modalData.value,
            cemetery: data?.data ?? null,
        }
        await fetchCemeteries()
        message.success('Cemitério cadastrado.')
    } catch (_) {
        message.error('Erro ao cadastrar.')
    } finally {
        formSaving.value = false
    }
}

// ---------- Listas para o formulário de sepultura ----------
async function loadCemeteriesForSepultureForm() {
    try {
        const { data } = await window.axios.get('/api/cemeteries/cemetery')
        cemeteriesListForSepulture.value = data?.data ?? []
    } catch (_) {
        cemeteriesListForSepulture.value = []
    }
}

async function loadSepultureTypesList() {
    try {
        const { data } = await window.axios.get('/api/cemeteries/sepulture-types')
        sepultureTypesList.value = data?.data ?? []
    } catch (_) {
        sepultureTypesList.value = []
    }
}

// ---------- Modal sepultura: abrir, fechar, salvar ----------
async function openSepultureModal(gid) {
    modalSepultureVisible.value = true
    modalSepultureLoading.value = true
    modalSepultureData.value = null
    isEditModeSepulture.value = false
    formStateSepulture.value = {
        description: '',
        block_location: '',
        lot_location: '',
        sepultures_quantity: 1,
        sepulture_type_code: undefined,
        cemetery_id: undefined,
    }
    await loadCemeteriesForSepultureForm()
    await loadSepultureTypesList()
    try {
        const { data } = await window.axios.get(`/api/cemeteries/sepulture/by-geo/${gid}`)
        const payload = data?.data ?? null
        modalSepultureData.value = payload
        if (payload?.sepulture) {
            const s = payload.sepulture
            formStateSepulture.value = {
                description: s.description ?? '',
                block_location: s.block_location ?? '',
                lot_location: s.lot_location ?? '',
                sepultures_quantity: s.sepultures_quantity ?? 1,
                sepulture_type_code: s.sepulture_type_code ?? undefined,
                cemetery_id: s.cemetery_id ?? undefined,
            }
        } else if (payload?.gid && selectedCemeteryGid.value != null) {
            const match = cemeteriesListForSepulture.value.find(
                (c) => c.geo_cemetery_gid === selectedCemeteryGid.value
            )
            if (match) formStateSepulture.value.cemetery_id = match.id
        }
    } catch (_) {
        modalSepultureData.value = { gid: Number(gid), sepulture: null }
        const match = cemeteriesListForSepulture.value.find(
            (c) => c.geo_cemetery_gid === selectedCemeteryGid.value
        )
        if (match) formStateSepulture.value.cemetery_id = match.id
    } finally {
        modalSepultureLoading.value = false
    }
    if (modalSepultureData.value?.sepulture?.id) loadBurials()
}

function closeSepultureModal() {
    modalSepultureVisible.value = false
    modalSepultureData.value = null
    isEditModeSepulture.value = false
}

function cancelEditSepulture() {
    isEditModeSepulture.value = false
    if (modalSepultureData.value?.sepulture) {
        const s = modalSepultureData.value.sepulture
        formStateSepulture.value = {
            description: s.description ?? '',
            block_location: s.block_location ?? '',
            lot_location: s.lot_location ?? '',
            sepultures_quantity: s.sepultures_quantity ?? 1,
            sepulture_type_code: s.sepulture_type_code ?? undefined,
            cemetery_id: s.cemetery_id ?? undefined,
        }
    }
}

async function saveSepulture() {
    if (!modalSepultureData.value?.sepulture?.id) return
    formSavingSepulture.value = true
    try {
        await window.axios.put(
            `/api/cemeteries/sepulture/${modalSepultureData.value.sepulture.id}`,
            {
                description: formStateSepulture.value.description || null,
                block_location: (formStateSepulture.value.block_location ?? '').trim(),
                lot_location: (formStateSepulture.value.lot_location ?? '').trim(),
                sepultures_quantity: formStateSepulture.value.sepultures_quantity,
                sepulture_type_code: formStateSepulture.value.sepulture_type_code,
                cemetery_id: formStateSepulture.value.cemetery_id,
                geo_sepulture_gid: modalSepultureData.value.gid,
            }
        )
        modalSepultureData.value = {
            ...modalSepultureData.value,
            sepulture: {
                ...modalSepultureData.value.sepulture,
                ...formStateSepulture.value,
            },
        }
        isEditModeSepulture.value = false
        message.success('Sepultura atualizada.')
    } catch (_) {
        message.error('Erro ao salvar.')
    } finally {
        formSavingSepulture.value = false
    }
}

async function createSepulture() {
    if (modalSepultureData.value?.gid == null) return
    formSavingSepulture.value = true
    try {
        const { data } = await window.axios.post('/api/cemeteries/sepulture', {
            description: formStateSepulture.value.description || null,
            block_location: (formStateSepulture.value.block_location ?? '').trim(),
            lot_location: (formStateSepulture.value.lot_location ?? '').trim(),
            sepultures_quantity: formStateSepulture.value.sepultures_quantity,
            sepulture_type_code: formStateSepulture.value.sepulture_type_code,
            cemetery_id: formStateSepulture.value.cemetery_id,
            geo_sepulture_gid: modalSepultureData.value.gid,
        })
        modalSepultureData.value = {
            ...modalSepultureData.value,
            sepulture: data?.data ?? null,
        }
        if (selectedCemeteryGid.value != null) {
            loadSepulturesForCemetery(selectedCemeteryGid.value)
        }
        message.success('Sepultura cadastrada.')
        if (modalSepultureData.value.sepulture?.id) loadBurials()
    } catch (_) {
        message.error('Erro ao cadastrar.')
    } finally {
        formSavingSepulture.value = false
    }
}

// ---------- Sepultamentos (burials) ----------
async function loadBurials() {
    const sepultureId = modalSepultureData.value?.sepulture?.id
    if (!sepultureId) return
    burialsLoading.value = true
    try {
        const { data } = await window.axios.get('/api/cemeteries/sepultamento', {
            params: { sepulture_id: sepultureId },
        })
        const list = (data?.data ?? []).map((b) => ({
            ...b,
            defunct: b.defunct?.name ?? '—',
            responsible: b.responsible?.name ?? '—',
            legist: b.legist?.name ?? '—',
        }))
        burialsList.value = list
    } catch (_) {
        burialsList.value = []
    } finally {
        burialsLoading.value = false
    }
}

async function loadPhysicalPeople(search = '') {
    physicalPeopleLoading.value = true
    try {
        const params = typeof search === 'string' && search.length > 0 ? { search: search.trim() } : {}
        const { data } = await window.axios.get('/api/register/physical-person', { params })
        physicalPeopleList.value = Array.isArray(data?.data) ? data.data : []
    } catch (_) {
        physicalPeopleList.value = []
    } finally {
        physicalPeopleLoading.value = false
    }
}

async function loadLegalPeople(search = '') {
    legalPeopleLoading.value = true
    try {
        const params = typeof search === 'string' && search.length > 0 ? { search: search.trim() } : {}
        const { data } = await window.axios.get('/api/register/legal-person', { params })
        legalPeopleList.value = Array.isArray(data?.data) ? data.data : []
    } catch (_) {
        legalPeopleList.value = []
    } finally {
        legalPeopleLoading.value = false
    }
}

async function openBurialModal(record = null) {
    burialModalEditingId.value = record ? record.id : null
    burialFormState.value = {
        causa_mortis: record?.causa_mortis ?? '',
        burial_date: record?.burial_date ?? null,
    }
    burialDefunctId.value = record?.defunct_id ?? undefined
    burialResponsibleId.value = record?.responsible_id ?? undefined
    burialLegistId.value = record?.legist_id ?? undefined
    burialHospitalId.value = record?.hospital_id ?? undefined
    burialFuneraryId.value = record?.funerary_id ?? undefined
    burialModalVisible.value = true
    await loadOccupiedDefunctIds(burialModalEditingId.value || null)
    await loadPhysicalPeople()
    await loadLegalPeople()
    if (burialDefunctId.value != null && isDefunctAlreadyBuried(burialDefunctId.value)) {
        burialDefunctId.value = undefined
    }
    if (burialModalEditingId.value) loadArchives()
    else archivesList.value = []
    pendingArchives.value = []
    newArchiveType.value = 'other'
}

function closeBurialModal() {
    burialModalVisible.value = false
    occupiedDefunctIds.value = new Set()
    burialModalEditingId.value = null
    burialFormState.value = {
        causa_mortis: '',
        burial_date: null,
    }
    burialDefunctId.value = undefined
    burialResponsibleId.value = undefined
    burialLegistId.value = undefined
    burialHospitalId.value = undefined
    burialFuneraryId.value = undefined
    archivesList.value = []
    pendingArchives.value = []
}

async function loadArchives() {
    const burialId = burialModalEditingId.value
    if (!burialId) return
    archivesLoading.value = true
    try {
        const { data } = await window.axios.get('/api/cemeteries/arquivos', {
            params: { burial_id: burialId },
        })
        archivesList.value = data?.data ?? []
    } catch (_) {
        archivesList.value = []
    } finally {
        archivesLoading.value = false
    }
}

function archiveTypeLabel(type) {
    const t = archiveTypeOptions.find((o) => o.value === type)
    return t ? t.label : type
}

function isImagePath(path) {
    if (!path) return false
    const ext = (path.split('.').pop() || '').toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
}

function isPdfPath(path) {
    if (!path) return false
    const ext = (path.split('.').pop() || '').toLowerCase()
    return ext === 'pdf'
}

function openArchiveUrl(url) {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function triggerArchiveFileInput() {
    archiveFileInputRef.value?.click()
}

function onArchiveFileChange(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!isArchiveTypeAllowedFile(file, newArchiveType.value)) {
        if (newArchiveType.value === 'photo') {
            message.warning('Para tipo Foto, selecione apenas arquivos de imagem.')
        } else if (newArchiveType.value === 'death_certificate') {
            message.warning('Para tipo Certidão de óbito, selecione apenas documentos (pdf, doc, docx, odt, rtf, txt).')
        }
        return
    }
    pendingArchives.value.push({
        file,
        archive_type: newArchiveType.value,
        name: file.name,
    })
}

function isArchiveTypeAllowedFile(file, archiveType) {
    const ext = (file?.name?.split('.').pop() || '').toLowerCase()
    const mime = (file?.type || '').toLowerCase()
    if (archiveType === 'photo') {
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
        return imageExts.includes(ext) || mime.startsWith('image/')
    }
    if (archiveType === 'death_certificate') {
        const docExts = ['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt']
        return docExts.includes(ext)
    }
    return true
}

function removePendingArchive(index) {
    pendingArchives.value.splice(index, 1)
}

async function uploadPendingArchives(burialId) {
    for (const item of pendingArchives.value) {
        const form = new FormData()
        form.append('burial_id', burialId)
        form.append('archive_type', item.archive_type)
        form.append('file', item.file)
        await window.axios.post('/api/cemeteries/arquivos', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    }
}

function confirmDeleteArchive(item) {
    if (!item?.id) return
    if (!window.confirm('Excluir este anexo?')) return
    window.axios
        .delete(`/api/cemeteries/arquivos/${item.id}`)
        .then(() => {
            message.success('Anexo excluído.')
            loadArchives()
        })
        .catch(() => message.error('Erro ao excluir anexo.'))
}

async function openArchivesViewModal(record) {
    if (!record?.id) return
    archivesViewModalVisible.value = true
    archivesViewLoading.value = true
    archivesViewList.value = []
    try {
        const { data } = await window.axios.get('/api/cemeteries/arquivos', {
            params: { burial_id: record.id },
        })
        archivesViewList.value = data?.data ?? []
    } catch (_) {
        archivesViewList.value = []
    } finally {
        archivesViewLoading.value = false
    }
}

function closeArchivesViewModal() {
    archivesViewModalVisible.value = false
    archivesViewList.value = []
}

function goToPrevArchivesSlide() {
    const carousel = archivesCarouselRef.value
    if (carousel?.prev) carousel.prev()
}

function goToNextArchivesSlide() {
    const carousel = archivesCarouselRef.value
    if (carousel?.next) carousel.next()
}

async function submitBurial() {
    const sepultureId = modalSepultureData.value?.sepulture?.id
    if (!sepultureId) return
    const defunctId = burialDefunctId.value
    const responsibleId = burialResponsibleId.value
    const legistId = burialLegistId.value
    if (defunctId === undefined || defunctId === null || defunctId === '' ||
        responsibleId === undefined || responsibleId === null || responsibleId === '' ||
        legistId === undefined || legistId === null || legistId === '') {
        message.warning('Preencha Falecido, Responsável e Legista.')
        return
    }
    burialSaving.value = true
    try {
        const payload = {
            sepulture_id: sepultureId,
            causa_mortis: burialFormState.value.causa_mortis || null,
            burial_date: burialFormState.value.burial_date || null,
            defunct_id: defunctId,
            responsible_id: responsibleId,
            legist_id: legistId,
            hospital_id: burialHospitalId.value ?? null,
            funerary_id: burialFuneraryId.value ?? null,
        }
        let burialId = burialModalEditingId.value
        if (burialId) {
            await window.axios.put(`/api/cemeteries/sepultamento/${burialId}`, payload)
            message.success('Sepultamento atualizado.')
        } else {
            const { data } = await window.axios.post('/api/cemeteries/sepultamento', payload)
            burialId = data?.data?.id ?? null
            message.success('Sepultamento incluído.')
        }
        if (burialId && pendingArchives.value.length) {
            await uploadPendingArchives(burialId)
            if (pendingArchives.value.length === 1) {
                message.success('Anexo adicionado.')
            } else if (pendingArchives.value.length > 1) {
                message.success(`${pendingArchives.value.length} anexos adicionados.`)
            }
        }
        loadBurials()
        pendingArchives.value = []
        closeBurialModal()
    } catch (err) {
        const errors = err.response?.data?.errors
        const msg =
            errors?.defunct_id?.[0]
            || errors?.sepulture_id?.[0]
            || err.response?.data?.message
            || 'Erro ao salvar sepultamento.'
        message.error(msg)
    } finally {
        burialSaving.value = false
    }
}

function confirmDeleteBurial(record) {
    if (!record?.id) return
    if (!window.confirm('Excluir este sepultamento?')) return
    window.axios
        .delete(`/api/cemeteries/sepultamento/${record.id}`)
        .then(() => {
            message.success('Sepultamento excluído.')
            loadBurials()
        })
        .catch(() => message.error('Erro ao excluir.'))
}

// ---------- Lifecycle ----------
onMounted(async () => {
    await loadHomeIconPersonalization()

    map = new OlMap({
        target: 'cemeteries-map',
        layers: [],
        view: new View({
            ...getDefaultMapView(),
        }),
        controls: defaultControls({ rotate: false, zoom: false }).extend([scaleLine]),
    })

    swipe = new Swipe({ position: 0.5 })
    map.addControl(swipe)

    const moduleLayers = await getRegisteredLayers('Cemiterio')
    ;(moduleLayers || []).forEach((layer) => {
        if (layer) map.addLayer(layer)
    })

    await fetchCemeterySubmenus()
    await fetchCemeteries()
    leftLayerDefs.value = [
        { key: 'cemiterio', title: 'Cemitério', make: () => createCemeteriesLayer(cemeteries.value) },
    ]
    rightLayerDefs.value = [
        { key: 'sepultura', title: 'Sepultura', make: () => createSepulturesLayer() },
    ]
    await applySwipeLayers()

    // Se existir apenas um cemitério, carregar como se tivesse clicado nele no painel (zoom + sepulturas)
    if (cemeteries.value.length === 1 && cemeteries.value[0]?.geom_geojson) {
        zoomToCemetery(cemeteries.value[0])
    }

    // Clique acompanha a posição do swipe: esquerda = cemitério, direita = sepultura
    map.on('singleclick', (evt) => {
        const size = map.getSize()
        if (!size || size[0] <= 0) return
        const position = swipe ? swipe.get('position') : 0.5
        const thresholdX = size[0] * position
        const clickLeft = evt.pixel[0] < thresholdX

        let gid = null
        if (clickLeft) {
            // Lado esquerdo: apenas camada de cemitério
            const cemeteryLayer = getCemeteryLayer()
            if (cemeteryLayer) {
                map.forEachFeatureAtPixel(
                    evt.pixel,
                    (feature) => {
                        if (gid == null) gid = feature.get('gid')
                    },
                    { layerFilter: (layer) => layer === cemeteryLayer, hitTolerance: 5 }
                )
                if (gid != null) openCemeteryModal(gid)
            }
        } else {
            // Lado direito: apenas camada de sepultura
            const sepultureLayer = getSepultureLayer()
            if (sepultureLayer) {
                map.forEachFeatureAtPixel(
                    evt.pixel,
                    (feature) => {
                        if (gid == null) gid = feature.get('gid')
                    },
                    { layerFilter: (layer) => layer === sepultureLayer, hitTolerance: 5 }
                )
                if (gid != null) openSepultureModal(gid)
            }
        }
    })

    mapRef.value = map;
    await nextTick()
    queueSwipeRefresh()
})
</script>

<style scoped>
.map-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
.map {
    width: 100%;
    height: 100%;
}
::v-deep(.ol-zoom) {
    position: absolute;
    right: 10px;
    left: auto !important;
    z-index: 1000;
}
::v-deep(.ol-layerswitcher) {
    position: absolute;
    top: 22px;
    right: 8px;
}

/* Consulta no mapa: topo do lado da camada sepultura (direita), à esquerda do layer switcher */
.map-consulta-wrapper {
    position: absolute;
    top: calc(8px + (var(--home-map-icon-size, 32px) * 2) + 20px);
    left: 8px;
    z-index: 1000;
    width: 320px;
    max-width: calc(50vw - 84px);
}
.map-consulta-search-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
}
.map-consulta-input {
    width: calc(100% - var(--home-map-icon-size, 32px) - 6px);
    border-radius: var(--home-map-icon-radius, 6px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
:deep(.map-consulta-input.ant-input),
:deep(.map-consulta-input.ant-input-affix-wrapper) {
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
}

:deep(.map-consulta-input.ant-input-affix-wrapper) {
    padding-top: 0;
    padding-bottom: 0;
}

:deep(.map-consulta-input.ant-input-affix-wrapper .ant-input) {
    height: 100%;
    line-height: normal;
}
.map-consulta-toggle-btn {
    flex-shrink: 0;
}
.map-consulta-loading,
.map-consulta-empty {
    margin-top: 6px;
    padding: 10px 12px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    font-size: 13px;
    color: #666;
}
.map-consulta-results {
    margin-top: 6px;
    max-height: 280px;
    overflow-y: auto;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.map-consulta-result-item {
    padding: 10px 12px;
    font-size: 13px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.15s;
}
.map-consulta-result-item:last-child {
    border-bottom: none;
}
.map-consulta-result-item:hover {
    background: #f5f5f5;
}
.map-consulta-label {
    font-weight: 600;
    color: #333;
}

/* Ícones dos submenus: mesmo estilo do HomeMap (canto superior esquerdo) */
.cemeteries-map-icons {
    position: absolute;
    top: calc(8px + var(--home-map-icon-size, 32px) + 12px);
    left: 8px;
    z-index: 1000;
}

.map-icons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, 0.6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, 0.7);
}

.menu-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.cemeteries-submenu-antd-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

/* Painel expansível: canto inferior esquerdo */
.cemeteries-panel {
    position: absolute;
    bottom: 20px;
    left: 20px;
    z-index: 1000;
    display: flex;
    align-items: flex-end;
    gap: 0;
}

/* Botão lista de cemitérios: mesmo padrão do ícone Tipos de Sepultura (map-icon-btn) */
.panel-toggle.map-icon-btn {
    flex-shrink: 0;
}

.panel-content {
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 50vh;
    min-width: 280px;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.panel-header {
    padding: 10px 14px;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid #eee;
    background: #fafafa;
}

.panel-loading,
.panel-empty {
    padding: 16px 14px;
    color: #666;
    font-size: 13px;
}

.cemeteries-list {
    list-style: none;
    margin: 0;
    padding: 8px 0;
    overflow-y: auto;
}

.cemetery-item {
    padding: 10px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cemetery-item:hover {
    background: #f5f5f5;
}
.cemetery-item:last-child {
    border-bottom: none;
}

.cemetery-name {
    font-size: 13px;
    color: #333;
}
.cemetery-address {
    font-size: 12px;
    color: #666;
}
.cemetery-address.no-address {
    font-style: italic;
    color: #999;
}

.cemetery-modal-view .cemetery-modal-actions {
    margin-top: 16px;
}
.cemetery-modal-consulta-only {
    color: #666;
    margin: 0;
}
.cemetery-modal-form .ant-form-item:last-child {
    margin-bottom: 0;
}

/* Tabela dados da sepultura: colunas com espaçamento igual */
.sepulture-view-table.ant-table table {
    table-layout: fixed;
    width: 100%;
}
.sepulture-view-table .ant-table-thead th,
.sepulture-view-table .ant-table-tbody td {
    width: 16.66%;
}

.sepulture-modal-view .sepulture-modal-actions {
    margin-top: 16px;
}
.sepulture-action-trigger {
    display: inline-block;
    line-height: 0;
}
.sepulture-action-btn {
    padding: 0;
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.sepulture-action-btn .fas {
    font-size: 14px;
}
.add-burial-btn.sepulture-action-btn {
    min-width: 32px;
    height: 32px;
    padding: 0;
}
.sepulture-modal-form .ant-form-item:last-child {
    margin-bottom: 0;
}

/* Modal sepultura: seções e organização */
.sepulture-form-section {
    margin-bottom: 20px;
}
.sepulture-form-section:last-of-type {
    margin-bottom: 0;
}
.sepulture-form-section-title {
    font-weight: 600;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid #f0f0f0;
}
.sepulture-form-actions {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}
.sepulture-view-descriptions {
    margin-bottom: 0;
}

.sepulture-burials-section {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}
.sepulture-burials-section .sepulture-form-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}
.add-burial-btn {
    flex-shrink: 0;
}

.select-not-found {
    padding: 8px 12px;
    font-size: 13px;
    color: #666;
}
.select-not-found a {
    margin-left: 4px;
    color: #1890ff;
}
.select-not-found a:hover {
    text-decoration: underline;
}

/* Anexos do sepultamento */
.burial-archives-list {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}
.burial-archive-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
    background: #fafafa;
    width: 140px;
}
.burial-archive-preview {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #eee;
}
.burial-archive-preview:hover {
    background: #e0e0e0;
}
.burial-archive-thumb {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
.burial-archive-icon {
    font-size: 32px;
    color: #999;
}
.burial-archive-meta {
    padding: 8px;
    font-size: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.burial-archive-type-label {
    font-weight: 600;
    color: #333;
}
.burial-archive-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}
.burial-archive-tooltip-trigger {
    display: inline-block;
    line-height: 0;
}
.burial-archive-btn-icon {
    padding: 0 4px;
    min-width: 24px;
    height: 24px;
}
.burial-archive-btn-icon .fas {
    font-size: 12px;
}
.burial-archives-empty {
    color: #999;
    font-size: 13px;
    padding: 8px 0;
}
.burial-pending-label {
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 8px;
}
.burial-pending-list {
    list-style: none;
    margin: 0 0 12px 0;
    padding: 0;
}
.burial-pending-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    font-size: 13px;
    border-bottom: 1px solid #f0f0f0;
}
.burial-pending-item:last-child {
    border-bottom: none;
}
.burial-pending-type {
    color: #666;
    font-size: 12px;
}
.burial-archive-upload {
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid #f0f0f0;
}
.burial-archive-upload-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.burial-archive-upload-label {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    margin: 0;
    flex-shrink: 0;
}
.burial-archive-type-select {
    width: 200px;
    flex-shrink: 0;
}
.burial-archive-file-input {
    display: none !important;
}

/* Tabela de sepultamentos: ações com ícones */
.burials-table-actions {
    display: flex;
    align-items: center;
    gap: 2px;
}
.burial-action-trigger {
    display: inline-block;
    line-height: 0;
}
.burial-action-btn {
    padding: 0 4px;
    min-width: 28px;
    height: 28px;
}
.burial-action-btn .fas {
    font-size: 14px;
}

/* Modal visualizar anexos: carrossel e setas */
.archives-view-carousel-wrap {
    padding: 8px 0;
}
.archives-view-carousel-inner {
    position: relative;
    width: 100%;
}
.archives-view-carousel {
    width: 100%;
}
.archives-view-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5) !important;
    color: #fff !important;
    border: none;
    border-radius: 50%;
}
.archives-view-arrow:hover {
    background: rgba(0, 0, 0, 0.75) !important;
    color: #fff !important;
}
.archives-view-arrow .fas {
    font-size: 18px;
}
.archives-view-arrow-prev {
    left: 12px;
}
.archives-view-arrow-next {
    right: 12px;
}
.archives-view-slide {
    padding: 12px;
}
.archives-view-slide-inner {
    min-height: min(70vh, 560px);
    height: min(70vh, 560px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
}
.archives-view-img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
}
.archives-view-pdf {
    width: 100%;
    height: 100%;
    min-height: 400px;
    border: none;
    border-radius: 4px;
}
.archives-view-file-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
}
.archives-view-file-icon {
    font-size: 64px;
    color: #999;
}
.archives-view-file-type {
    font-size: 14px;
    color: #666;
}
.archives-view-slide-caption {
    text-align: center;
    margin-top: 8px;
    font-size: 13px;
    color: #666;
}
.archives-view-empty {
    padding: 24px;
    text-align: center;
    color: #999;
}
.archives-view-modal-footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.cadastro-person-component-wrapper {
    min-height: 120px;
}
.cadastro-person-modal-footer {
    padding: 12px 16px;
    border-top: 1px solid #f0f0f0;
    margin-top: 16px;
}

/* Modal sepultura: conteúdo em 2 colunas; altura limitada */
.sepulture-modal-scroll {
    max-height: min(65vh, 480px);
    overflow-y: auto;
}

.menu-route-component-wrapper {
    min-height: 120px;
}

.menu-route-no-component {
    color: #666;
    margin-top: 12px;
}
</style>

<!-- Modais renderizados no body: z-index e posição/scroll -->
<style>
.menu-route-modal-wrap .menu-route-modal-scroll {
    max-height: 75vh;
    overflow: auto;
    padding: 16px;
    padding-bottom: 72px;
}

.menu-route-modal-wrap .menu-route-float-btn {
    position: absolute;
    right: 20px;
    bottom: 20px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.cadastro-person-modal-wrap {
    z-index: 9999 !important;
}

/* Modal dados da sepultura: mais largo, no topo, com scroll */
.sepulture-modal-wrap.ant-modal-wrap {
    padding-top: 24px;
    padding-bottom: 24px;
    overflow: hidden;
}
.sepulture-modal-wrap .ant-modal {
    top: 24px !important;
    transform: none !important;
    max-height: calc(100vh - 48px) !important;
    padding-bottom: 24px;
}
.sepulture-modal-wrap .ant-modal-content {
    max-height: calc(100vh - 48px) !important;
    display: flex !important;
    flex-direction: column !important;
}
.sepulture-modal-wrap .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto !important;
    overflow-x: hidden !important;
}

/* Modal visualizar anexos: mais no topo, sem exceder altura da tela */
.archives-view-modal-wrap.ant-modal-wrap {
    padding-top: 12px;
    padding-bottom: 12px;
    overflow: hidden;
}
.archives-view-modal-wrap .ant-modal {
    top: 12px !important;
    transform: none !important;
    max-height: calc(100vh - 24px) !important;
    padding-bottom: 12px;
}
.archives-view-modal-wrap .ant-modal-content {
    max-height: calc(100vh - 24px) !important;
    display: flex !important;
    flex-direction: column !important;
}
.archives-view-modal-wrap .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto !important;
    overflow-x: hidden !important;
}

/* Modal sepultamento: mais no topo, altura máxima e scroll no conteúdo */
.burial-modal-wrap.ant-modal-wrap {
    padding-top: 24px;
    padding-bottom: 24px;
    overflow: hidden;
}
.burial-modal-wrap .ant-modal {
    top: 24px !important;
    transform: none !important;
    max-height: calc(100vh - 48px) !important;
    padding-bottom: 24px;
}
.burial-modal-wrap .ant-modal-content {
    max-height: calc(100vh - 48px) !important;
    display: flex !important;
    flex-direction: column !important;
}
.burial-modal-wrap .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto !important;
    overflow-x: hidden !important;
}
</style>
